import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Clock,
  Clock3,
  ExternalLink,
  Info,
  LayoutGrid,
  List,
  Music2,
  Package,
  Plus,
  Search as SearchIcon,
  ShieldAlert,
  Users,
  X,
  Zap,
} from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import AddCompetitorModal from './AddCompetitorModal'
import CompetitorDetailPanel from './CompetitorDetailPanel'
import { PredictionBadge } from './PredictionBadge'
import PurchaseIntentIndicator from './PurchaseIntentIndicator'
import { Skeleton, ThreatCardSkeleton } from './Skeleton'
import { EmptyState } from '../ui/EmptyState'
import { useActions, useCompetitorDetail, useCompetitors, usePredictions, useTikTokIntel } from '../../hooks/useCompetitiveData'
import type { Action, CompetitorDetailRow, CompetitorRow } from '../../types/competitive'
import { generateSignalSummary } from '../../lib/competitive-utils'
import { canonicalBrandKey, canonicalBrandName } from '../../lib/utils'

type CompetitorTierFilter = 'all' | 'critical' | 'active' | 'watch' | 'low'
type CompetitorSort = 'score_desc' | 'score_asc' | 'name_asc' | 'name_desc' | 'bsr_rank'
type CompetitorView = 'card' | 'table'
type SparklineKind = 'score' | 'bsr'

function formatDate(value: string | null): string {
  if (!value) {
    return '—'
  }

  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function normalizeNumericValue(value: number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null
  }
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return null
  }
  return parsed
}

function formatRank(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return 'Not available'
  }
  return `#${Math.round(value).toLocaleString('en-US')}`
}

function estimateMonthlySales(bsr: number | null): string {
  if (!bsr || bsr <= 0) return ''
  if (bsr <= 50) return '~50K+ units/mo'
  if (bsr <= 200) return '~15K–50K/mo'
  if (bsr <= 500) return '~5K–15K/mo'
  if (bsr <= 1000) return '~2K–5K/mo'
  if (bsr <= 3000) return '~800–2K/mo'
  if (bsr <= 7000) return '~300–800/mo'
  if (bsr <= 15000) return '~100–300/mo'
  if (bsr <= 40000) return '~30–100/mo'
  if (bsr <= 100000) return '~10–30/mo'
  return '<10/mo'
}

function threatScoreClasses(score: number): { panel: string; ring: string; text: string; cardBg: string; cardHover: string } {
  if (score >= 80) {
    return {
      panel: 'border-l-[#991b1b]',
      ring: 'border-[#991b1b] bg-[#fee2e2]',
      text: 'text-[#991b1b] font-extrabold',
      cardBg: 'bg-red-50/50',
      cardHover: 'hover:bg-red-50/70',
    }
  }
  if (score >= 60) {
    return {
      panel: 'border-l-[#dc2626]',
      ring: 'border-red-300 bg-red-50',
      text: 'text-[#dc2626]',
      cardBg: 'bg-red-50/40',
      cardHover: 'hover:bg-red-50/60',
    }
  }
  if (score >= 40) {
    return {
      panel: 'border-l-[#d97706]',
      ring: 'border-amber-300 bg-amber-50',
      text: 'text-[#d97706]',
      cardBg: 'bg-amber-50/30',
      cardHover: 'hover:bg-amber-50/50',
    }
  }
  if (score >= 20) {
    return {
      panel: 'border-l-[#2563eb]',
      ring: 'border-blue-300 bg-blue-50',
      text: 'text-[#2563eb]',
      cardBg: 'bg-blue-50/20',
      cardHover: 'hover:bg-blue-50/40',
    }
  }
  return {
    panel: 'border-l-[var(--gray-300)]',
    ring: 'border-[var(--gray-300)] bg-[var(--gray-100)]',
    text: 'text-[var(--gray-400)]',
    cardBg: 'bg-white',
    cardHover: 'hover:bg-slate-50',
  }
}

function decisionBadgeMeta(score: number): { label: string; pill: string } {
  if (score >= 80) {
    return { label: 'Critical', pill: 'text-[#991b1b]' }
  }
  if (score >= 60) {
    return { label: 'High', pill: 'text-[#dc2626]' }
  }
  if (score >= 40) {
    return { label: 'Elevated', pill: 'text-[#d97706]' }
  }
  if (score >= 20) {
    return { label: 'Watching', pill: 'text-[#2563eb]' }
  }
  return { label: 'Monitoring', pill: 'text-[var(--gray-500)]' }
}

function threatTierMeta(threatTier: string | null): { label: string; dot: string; pill: string } {
  const normalized = (threatTier ?? '').trim().toLowerCase()
  if (normalized === 'critical') {
    return {
      label: 'Critical',
      dot: 'bg-[#991b1b]',
      pill: 'border-[#991b1b] bg-[#991b1b] text-white',
    }
  }

  if (normalized === 'high') {
    return {
      label: 'High',
      dot: 'bg-[#dc2626]',
      pill: 'border-[#991b1b] bg-[#fee2e2] text-[#991b1b]',
    }
  }

  if (normalized === 'active') {
    return {
      label: 'Active Threat',
      dot: 'bg-[#d97706]',
      pill: 'border-[#d97706] bg-[#fef3c7] text-[#92400e]',
    }
  }

  return {
    label: normalized === 'low' ? 'Low' : 'Monitoring',
    dot: 'bg-[var(--gray-400)]',
    pill: 'border-[var(--gray-200)] bg-[var(--gray-100)] text-[var(--gray-500)]',
  }
}

function normalizeTier(alertTier: string | null): Exclude<CompetitorTierFilter, 'all'> {
  const normalized = (alertTier ?? '').toLowerCase()
  if (normalized.includes('critical')) return 'critical'
  if (normalized.includes('active') || normalized.includes('high')) return 'active'
  if (normalized.includes('watch') || normalized.includes('medium')) return 'watch'
  return 'low'
}

function tierBadgeMeta(alertTier: string | null): { label: string; badge: string } {
  const tier = normalizeTier(alertTier)
  if (tier === 'critical') {
    return {
      label: 'Critical',
      badge: 'border-[#991b1b] bg-[#991b1b] text-white',
    }
  }

  if (tier === 'active') {
    return {
      label: 'Active Threat',
      badge: 'border-[#d97706] bg-[#fef3c7] text-[#92400e]',
    }
  }

  if (tier === 'watch') {
    return {
      label: 'Monitoring',
      badge: 'border-[var(--gray-200)] bg-[var(--gray-100)] text-[var(--gray-500)]',
    }
  }

  return {
    label: 'Low',
    badge: 'border-[var(--gray-200)] bg-[var(--gray-100)] text-[var(--gray-500)]',
  }
}

function truncateSignalForTable(summary: string): string {
  const firstLine = cleanSignal(summary).split('\n')[0]?.trim() ?? ''
  if (!firstLine) {
    return 'No signals detected yet'
  }

  if (firstLine.length <= 60) {
    return firstLine
  }

  return `${firstLine.slice(0, 57)}...`
}

function trendStrokeColor(values: number[], kind: SparklineKind): string {
  if (values.length < 2) {
    return '#64748b'
  }

  const start = values[0]
  const end = values[values.length - 1]

  if (kind === 'bsr') {
    return end <= start ? '#16a34a' : '#dc2626'
  }

  return end >= start ? '#16a34a' : '#dc2626'
}

function formatSparklineLabel(value: number, kind: SparklineKind): string {
  if (kind === 'bsr') {
    return formatRank(value)
  }
  return Math.round(value).toLocaleString('en-US')
}

function Sparkline({
  values,
  kind,
  width = 140,
  height = 56,
  compact = false,
}: {
  values: number[]
  kind: SparklineKind
  width?: number
  height?: number
  compact?: boolean
}) {
  if (values.length === 0) {
    return <div className={`${compact ? 'h-4 w-10' : 'h-14 w-full'} rounded bg-slate-100`} />
  }

  const min = Math.min(...values)
  const max = Math.max(...values)
  const spread = Math.max(max - min, 1)
  const stroke = trendStrokeColor(values, kind)

  const points = values
    .map((value, index) => {
      const x = (index / Math.max(values.length - 1, 1)) * width
      const y = height - ((value - min) / spread) * height
      return `${x},${y}`
    })
    .join(' ')

  return (
    <div className={compact ? 'w-10' : 'relative w-full pb-4 pt-2'}>
      <svg viewBox={`0 0 ${width} ${height}`} className={compact ? 'h-4 w-10' : 'h-14 w-full'}>
        <polyline
          fill="none"
          stroke={stroke}
          strokeWidth={compact ? 1.75 : 2}
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
      </svg>

      {!compact && (
        <>
          <span className="pointer-events-none absolute bottom-0 left-0 text-[11px] text-slate-400">
            {formatSparklineLabel(min, kind)}
          </span>
          <span className="pointer-events-none absolute right-0 top-0 text-[11px] text-slate-400">
            {formatSparklineLabel(max, kind)}
          </span>
        </>
      )}
    </div>
  )
}

function pickScoreValue(...values: Array<number | null | undefined>): number {
  for (const value of values) {
    const parsed = normalizeNumericValue(value)
    if (parsed !== null) {
      return Math.max(0, parsed)
    }
  }
  return 0
}

function InfoTooltip({
  content,
  widthClass = 'w-[220px]',
  iconClassName = 'h-3.5 w-3.5 text-slate-400',
}: {
  content: string[]
  widthClass?: string
  iconClassName?: string
}) {
  return (
    <span className="group relative inline-flex items-center">
      <Info className={iconClassName} aria-hidden="true" />
      <span
        className={`pointer-events-none absolute left-1/2 top-full z-20 mt-2 ${widthClass} -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100`}
      >
        {content.map((line, index) => (
          <span key={`${line}-${index}`} className="block">
            {line}
          </span>
        ))}
      </span>
    </span>
  )
}

function ScoreBreakdownBar({
  label,
  value,
  max,
  valueLabel,
  tooltipText,
}: {
  label: string
  value: number
  max: number
  valueLabel?: string
  tooltipText?: string
}) {
  const bounded = Math.min(Math.max(value, 0), max)
  const widthPercent = (bounded / max) * 100

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-1">
          <span className="text-slate-700">{label}</span>
          {tooltipText ? <InfoTooltip content={[tooltipText]} iconClassName="h-3 w-3 text-slate-400" /> : null}
        </div>
        {valueLabel ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
            <Clock className="h-3 w-3" />
            Coming Soon
          </span>
        ) : (
          <span className="font-medium text-slate-900">
            {bounded.toFixed(1)} / {max}
          </span>
        )}
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-slate-800 transition-all" style={{ width: `${widthPercent}%` }} />
      </div>
    </div>
  )
}

function CompetitorDetailDrawer({
  open,
  competitor,
  detail,
  loading,
  error,
  onClose,
}: {
  open: boolean
  competitor: CompetitorRow | null
  detail: CompetitorDetailRow | null
  loading: boolean
  error: string | null
  onClose: () => void
}) {
  const drawerRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const drawerNode = drawerRef.current
    if (!drawerNode) {
      return
    }

    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    const getFocusable = () =>
      Array.from(drawerNode.querySelectorAll<HTMLElement>(focusableSelector)).filter(
        (element) => element.offsetParent !== null
      )

    const focusableElements = getFocusable()
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    } else {
      drawerNode.focus()
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const focusTargets = getFocusable()
      if (focusTargets.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusTargets[0]
      const last = focusTargets[focusTargets.length - 1]
      const active = document.activeElement

      if (event.shiftKey) {
        if (active === first || !focusTargets.includes(active as HTMLElement)) {
          event.preventDefault()
          last.focus()
        }
        return
      }

      if (active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    drawerNode.addEventListener('keydown', handleKeyDown)
    return () => {
      drawerNode.removeEventListener('keydown', handleKeyDown)
      previousFocusRef.current?.focus()
    }
  }, [onClose, open])

  const resolvedCompetitor = detail ?? competitor
  const threatScore = Number(resolvedCompetitor?.threat_score ?? 0)
  const threatClasses = threatScoreClasses(threatScore)
  const threatTier = threatTierMeta(resolvedCompetitor?.threat_tier ?? null)
  const normalizedHandle = (resolvedCompetitor?.tiktok_handle ?? '').replace(/^@/, '').trim()

  const bsrTrendSource = (detail?.bsr_trend_30d?.length ?? 0) > 0
    ? detail?.bsr_trend_30d ?? []
    : competitor?.bsr_trend_30d ?? []

  const amazonRankTrend = bsrTrendSource
    .map((point) => ({
      day: formatDate(point.day),
      rank: Number(point.avg_bsr),
    }))
    .filter((point) => Number.isFinite(point.rank))

  const scoreHistorySource = (detail?.score_history?.length ?? 0) > 0
    ? detail?.score_history ?? []
    : competitor?.score_history ?? []

  const scoreHistory = scoreHistorySource.map((point) => ({
    ...point,
    total_score: Number(point.total_score ?? 0),
  }))
  const recentScoreWindow = scoreHistory.slice(-7)
  const recentScoreValues = recentScoreWindow.map((point) => Number(point.total_score ?? 0))
  const recentScoreMin = recentScoreValues.length > 0 ? Math.min(...recentScoreValues) : 0
  const recentScoreMax = recentScoreValues.length > 0 ? Math.max(...recentScoreValues) : 0

  const latestScore =
    scoreHistory.length > 0
      ? scoreHistory[scoreHistory.length - 1]
      : null

  const breakdownRows = [
    {
      label: 'Amazon Sales Performance',
      value: pickScoreValue(latestScore?.bsr_velocity_score, detail?.bsr_velocity_score, competitor?.bsr_velocity_score),
      max: 25,
    },
    {
      label: 'Branded Search Presence',
      value: pickScoreValue(latestScore?.keyword_share_score, detail?.keyword_share_score, competitor?.keyword_share_score),
      max: 30,
      valueLabel: 'Phase 2',
      tooltipText: 'Requires SP-API Brand Analytics data (Phase 2 feature)',
    },
    {
      label: 'TikTok Momentum',
      value: pickScoreValue(latestScore?.tiktok_velocity_score, detail?.tiktok_velocity_score, competitor?.tiktok_velocity_score),
      max: 20,
    },
    {
      label: 'Negative Mentions',
      value: pickScoreValue(
        latestScore?.negative_mention_score,
        detail?.negative_mention_score,
        competitor?.negative_mention_score
      ),
      max: 15,
    },
    {
      label: 'Google Ads Activity',
      value: pickScoreValue(latestScore?.google_shopping_score, detail?.google_shopping_score, competitor?.google_shopping_score),
      max: 10,
      valueLabel: 'Phase 2',
      tooltipText: 'Google Shopping ad presence tracking (Phase 2)',
    },
  ]

  const trackedSince = resolvedCompetitor?.added_at ?? competitor?.added_at ?? null
  const amazonSearchBrand = (resolvedCompetitor?.amazon_brand_name || resolvedCompetitor?.brand_name || '').trim()
  const drawerTitle = resolvedCompetitor?.brand_name ?? 'Competitor Details'

  return (
    <div className={`fixed inset-0 z-50 ${open ? 'pointer-events-auto' : 'pointer-events-none'}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-slate-900/30 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      <aside
        ref={drawerRef}
        tabIndex={-1}
        className={`absolute inset-y-0 right-0 h-full w-full max-w-4xl overflow-y-auto bg-white shadow-2xl transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 p-5 backdrop-blur">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-900">{drawerTitle}</h3>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${threatTier.pill}`}
                >
                  <span className={`h-2 w-2 rounded-full ${threatTier.dot}`} />
                  {threatTier.label}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-500">Competitor detail view</p>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`flex h-20 w-20 flex-col items-center justify-center rounded-full border-4 ${threatClasses.ring}`}
              >
                <span className={`text-2xl font-bold leading-none ${threatClasses.text}`}>
                  {Math.round(threatScore)}
                </span>
                <span className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Threat
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-200 bg-white p-2 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                aria-label="Close competitor details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6 p-5">
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <Skeleton className="mb-2 h-4 w-56" />
              <Skeleton className="h-4 w-40" />
            </div>
          )}

          {resolvedCompetitor ? (
            <>
              <section className="rounded-lg border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-900">Threat Score Breakdown</h4>
            <p className="mt-1 text-xs text-slate-500">How the threat score is calculated today</p>
            <div className="mt-4 space-y-3">
              {breakdownRows.map((row) => (
                <ScoreBreakdownBar
                  key={row.label}
                  label={row.label}
                  value={row.value}
                  max={row.max}
                  valueLabel={row.valueLabel}
                  tooltipText={row.tooltipText}
                />
              ))}
            </div>
              </section>

              <section className="rounded-lg border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-900">Amazon Ranking History</h4>
            {amazonRankTrend.length > 0 ? (
              <>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={amazonRankTrend} margin={{ top: 8, right: 20, left: 10, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} minTickGap={24} />
                      <YAxis
                        reversed
                        tick={{ fontSize: 11, fill: '#64748b' }}
                        tickFormatter={(value) => formatRank(Number(value))}
                        width={86}
                        label={{
                          value: 'Amazon Rank',
                          angle: -90,
                          position: 'insideLeft',
                          fill: '#64748b',
                          fontSize: 11,
                        }}
                      />
                      <RechartsTooltip
                        formatter={(value: number) => [formatRank(Number(value)), 'Amazon Rank']}
                        labelFormatter={(label: string) => `Date: ${label}`}
                      />
                      <Line type="monotone" dataKey="rank" stroke="#0f766e" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Amazon Rank (lower is better)
                </p>
              </>
            ) : (
              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                No Amazon ranking data available yet — check back after the next scheduled scan.
              </div>
            )}
              </section>

              <section className="rounded-lg border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-900">Recent Activity</h4>
            {recentScoreValues.length > 0 ? (
              <div className="mt-3 space-y-2">
                <Sparkline values={recentScoreValues} kind="score" />
                <p className="text-sm text-slate-700">
                  Threat score over last 7 days: {Math.round(recentScoreMin)} → {Math.round(recentScoreMax)}
                </p>
              </div>
            ) : (
              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                No recent threat score history yet.
              </div>
            )}
              </section>

              <section className="rounded-lg border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-900">Competitor Profile</h4>
            <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-slate-700 md:grid-cols-2">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Brand name</div>
                <div>{resolvedCompetitor.brand_name}</div>
              </div>
              {resolvedCompetitor.amazon_brand_name &&
                resolvedCompetitor.amazon_brand_name !== resolvedCompetitor.brand_name && (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Amazon brand name</div>
                    <div>{resolvedCompetitor.amazon_brand_name}</div>
                  </div>
                )}
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">TikTok handle</div>
                <div>
                  {normalizedHandle ? (
                    <a
                      href={`https://www.tiktok.com/@${normalizedHandle}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-blue-500 underline-offset-2 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    >
                      @{normalizedHandle}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    'Not set'
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Monitoring tier</div>
                <div>{threatTier.label}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-xs uppercase tracking-wide text-slate-500">Notes</div>
                <div>{resolvedCompetitor.notes || 'No notes available'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500">Tracked since</div>
                <div>{formatDate(trackedSince)}</div>
              </div>
            </div>
              </section>

              <section className="rounded-lg border border-slate-200 p-4">
            <h4 className="font-semibold text-slate-900">Quick Links</h4>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={`https://www.amazon.com/s?k=${encodeURIComponent(amazonSearchBrand)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-500 underline-offset-2 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                Amazon Search
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              {normalizedHandle && (
                <a
                  href={`https://www.tiktok.com/@${normalizedHandle}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-500 underline-offset-2 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                >
                  TikTok Profile
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
              </section>
            </>
          ) : (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}
        </div>
      </aside>
    </div>
  )
}

function cleanSignal(text: string): string {
  return text
    .replace(/\(current[\s\S]*?baseline[\s\S]*?\)/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

function channelIcon(channel: string | null) {
  if (channel === 'amazon') return <Package className="h-3 w-3" />
  if (channel === 'tiktok') return <Music2 className="h-3 w-3" />
  if (channel === 'google') return <SearchIcon className="h-3 w-3" />
  return <Zap className="h-3 w-3" />
}

function actionTimestamp(action: Action): number {
  const raw = action.executed_at || action.updated_at || action.created_at || null
  if (!raw) return 0
  const parsed = new Date(raw).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

function parseCreatorCount(text: string): number {
  const mentionMatch = text.match(/(\d+)\s*mentions?\s*from\s*(\d+)\s*(?:unique\s*)?creators?/i)
  if (mentionMatch?.[2]) {
    return Number(mentionMatch[2])
  }
  const creatorMatch = text.match(/(\d+)\s*(?:macro\/mega|macro|mega|unique)?\s*creators?/i)
  if (creatorMatch?.[1]) {
    return Number(creatorMatch[1])
  }
  return 0
}

function parsePaidDisclosureRate(text: string): number | null {
  const explicit = text.match(/paid disclosure rate[=:]?\s*([\d.]+)%/i)
  if (explicit?.[1]) {
    const parsed = Number(explicit[1])
    return Number.isFinite(parsed) ? parsed : null
  }
  const shorthand = text.match(/([\d.]+)%\s*paid/i)
  if (shorthand?.[1]) {
    const parsed = Number(shorthand[1])
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

interface CompetitorTabProps {
  competitorFilter?: string
  setCompetitorFilter?: (value: string) => void
}

export default function CompetitorTab({
  competitorFilter = '',
  setCompetitorFilter,
}: CompetitorTabProps) {
  const [detailCompetitorId, setDetailCompetitorId] = useState<number | null>(null)
  const [detailCompetitorName, setDetailCompetitorName] = useState('')
  const [selectedCompetitor, setSelectedCompetitor] = useState<CompetitorRow | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState(competitorFilter)
  const [tierFilter, setTierFilter] = useState<CompetitorTierFilter>('all')
  const [sortOption, setSortOption] = useState<CompetitorSort>('score_desc')
  const [view, setView] = useState<CompetitorView>('card')
  const { data, isLoading, error } = useCompetitors()
  const { data: actionsData } = useActions()
  const { data: tiktokIntelData } = useTikTokIntel()
  const { predictions } = usePredictions()
  const {
    data: detailData,
    isLoading: detailLoading,
    error: detailError,
  } = useCompetitorDetail(selectedCompetitor?.id ?? null)

  useEffect(() => {
    if (!isDrawerOpen) {
      return
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDrawerOpen(false)
      }
    }

    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [isDrawerOpen])

  useEffect(() => {
    if (isDrawerOpen) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setSelectedCompetitor(null)
    }, 200)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [isDrawerOpen])

  useEffect(() => {
    setSearchTerm(competitorFilter)
  }, [competitorFilter])

  const openCompetitorDrawer = (competitor: CompetitorRow) => {
    setSelectedCompetitor(competitor)
    setIsDrawerOpen(true)
  }

  const openCompetitorProfile = (competitor: CompetitorRow) => {
    setDetailCompetitorId(competitor.id)
    setDetailCompetitorName(competitor.brand_name)
  }

  const competitors = useMemo(() => {
    if (!data || !Array.isArray(data.competitors)) {
      return []
    }

    const deduped = new Map<string, CompetitorRow>()
    for (const competitor of data.competitors) {
      const canonicalName = canonicalBrandName(competitor.brand_name)
      const key = canonicalBrandKey(canonicalName)
      const normalizedCompetitor: CompetitorRow = {
        ...competitor,
        brand_name: canonicalName,
        amazon_brand_name: competitor.amazon_brand_name ? canonicalBrandName(competitor.amazon_brand_name) : null,
      }

      const existing = deduped.get(key)
      if (!existing || Number(normalizedCompetitor.threat_score ?? 0) > Number(existing.threat_score ?? 0)) {
        deduped.set(key, normalizedCompetitor)
      }
    }

    return Array.from(deduped.values())
  }, [data])

  const competitorsError = error instanceof Error ? error.message : null
  const competitorDetailError = detailError instanceof Error ? detailError.message : null
  const selectedCompetitorDetail = detailData?.competitor ?? null
  const purchaseIntentByCompetitor = useMemo(() => {
    const aggregate = new Map<number, { sum: number; count: number }>()
    const rows = tiktokIntelData?.purchaseIntentVideos ?? []

    for (const row of rows) {
      if (!row.competitor_id) {
        continue
      }
      const current = aggregate.get(row.competitor_id) ?? { sum: 0, count: 0 }
      current.sum += Number(row.purchase_intent_score ?? 0)
      current.count += 1
      aggregate.set(row.competitor_id, current)
    }

    const averages = new Map<number, number>()
    for (const [competitorId, value] of aggregate.entries()) {
      if (value.count > 0) {
        averages.set(competitorId, value.sum / value.count)
      }
    }

    return averages
  }, [tiktokIntelData?.purchaseIntentVideos])

  const pendingActionCountByCompetitor = useMemo(() => {
    const map = new Map<number, number>()
    const actions = actionsData?.actions ?? []
    for (const action of actions) {
      const status = String(action.status ?? '').toLowerCase()
      if (!action.competitor_id || status === 'done' || status === 'completed') {
        continue
      }
      map.set(action.competitor_id, (map.get(action.competitor_id) ?? 0) + 1)
    }
    return map
  }, [actionsData?.actions])

  const creatorSignalByCompetitor = useMemo(() => {
    const map = new Map<number, { creatorCount: number; paidDisclosureRate: number | null }>()
    const actions = [...(actionsData?.actions ?? [])].sort((left, right) => actionTimestamp(right) - actionTimestamp(left))

    for (const action of actions) {
      if (!action.competitor_id || map.has(action.competitor_id)) {
        continue
      }
      if (!String(action.action_type || '').toLowerCase().includes('creator')) {
        continue
      }

      const text = `${action.finding || ''} ${action.action_description || ''}`.trim()
      const creatorCount = parseCreatorCount(text)
      const paidDisclosureRate = parsePaidDisclosureRate(text)

      if (creatorCount <= 0 && paidDisclosureRate === null) {
        continue
      }

      map.set(action.competitor_id, { creatorCount, paidDisclosureRate })
    }

    return map
  }, [actionsData?.actions])

  const filteredAndSortedCompetitors = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase()
    const filtered = competitors.filter((competitor) => {
      const matchesSearch =
        normalizedSearchTerm.length === 0 ||
        competitor.brand_name.toLowerCase().includes(normalizedSearchTerm)
      const matchesTier =
        tierFilter === 'all' || normalizeTier(competitor.alert_tier) === tierFilter

      return matchesSearch && matchesTier
    })

    const sorted = [...filtered].sort((a, b) => {
      if (sortOption === 'score_desc') {
        return Number(b.threat_score ?? 0) - Number(a.threat_score ?? 0)
      }

      if (sortOption === 'score_asc') {
        return Number(a.threat_score ?? 0) - Number(b.threat_score ?? 0)
      }

      if (sortOption === 'name_asc') {
        return a.brand_name.localeCompare(b.brand_name)
      }

      if (sortOption === 'name_desc') {
        return b.brand_name.localeCompare(a.brand_name)
      }

      const aRank = normalizeNumericValue(a.latest_bsr_overall)
      const bRank = normalizeNumericValue(b.latest_bsr_overall)
      if (aRank === null && bRank === null) return a.brand_name.localeCompare(b.brand_name)
      if (aRank === null) return 1
      if (bRank === null) return -1
      return aRank - bRank
    })

    return sorted
  }, [competitors, searchTerm, sortOption, tierFilter])
  const hasFilterMatches = filteredAndSortedCompetitors.length > 0

  if (detailCompetitorId !== null) {
    const fallbackName =
      detailCompetitorName ||
      competitors.find((competitor) => competitor.id === detailCompetitorId)?.brand_name ||
      'Competitor'

    return (
      <CompetitorDetailPanel
        competitorId={detailCompetitorId}
        competitorName={fallbackName}
        onBack={() => setDetailCompetitorId(null)}
      />
    )
  }

  return (
    <>
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-slate-500" />
            <h2 className="text-lg font-semibold text-slate-900">Competitors</h2>
            <InfoTooltip
              content={[
                '≥ 80: Critical (dark red)',
                '60–79: High (red)',
                '40–59: Elevated (amber)',
                '20–39: Watching (blue)',
                '< 20: Monitoring (neutral)',
              ]}
              widthClass="w-[260px]"
              iconClassName="h-4 w-4 cursor-help text-slate-400"
            />
          </div>

          <button
            type="button"
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--patterniq-navy-900)] bg-[var(--patterniq-navy-900)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--patterniq-navy-800)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Competitor
          </button>
        </div>

        {competitorsError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{competitorsError}</div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            <ThreatCardSkeleton />
            <ThreatCardSkeleton />
            <ThreatCardSkeleton />
          </div>
        ) : competitors.length === 0 ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title="No competitors tracked yet"
            description="Start by promoting emerging brands or adding competitors manually."
            action={(
              <button
                type="button"
                onClick={() => setIsAddModalOpen(true)}
                className="rounded-md border border-[var(--patterniq-navy-900)] bg-[var(--patterniq-navy-900)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--patterniq-navy-800)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                Add Competitor
              </button>
            )}
          />
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="relative min-w-[220px] flex-1">
                <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => {
                    const nextValue = event.target.value
                    setSearchTerm(nextValue)
                    setCompetitorFilter?.(nextValue)
                  }}
                  placeholder="Search competitors..."
                  className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-8 pr-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>

              <select
                value={tierFilter}
                onChange={(event) => setTierFilter(event.target.value as CompetitorTierFilter)}
                className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                <option value="all">All Tiers</option>
                <option value="critical">Critical</option>
                <option value="active">Active</option>
                <option value="watch">Monitoring</option>
                <option value="low">Low / Monitoring</option>
              </select>

              <select
                value={sortOption}
                onChange={(event) => setSortOption(event.target.value as CompetitorSort)}
                className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                <option value="score_desc">Highest Score</option>
                <option value="score_asc">Lowest Score</option>
                <option value="name_asc">Name A-Z</option>
                <option value="name_desc">Name Z-A</option>
                <option value="bsr_rank">BSR Rank</option>
              </select>

              <div className="ml-auto inline-flex items-center rounded-md border border-slate-200 bg-white p-1">
                <button
                  type="button"
                  onClick={() => setView('card')}
                  className={`rounded p-1.5 ${
                    view === 'card'
                      ? 'bg-[var(--patterniq-navy-900)] text-white'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`}
                  aria-label="Card view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setView('table')}
                  className={`rounded p-1.5 ${
                    view === 'table'
                      ? 'bg-[var(--patterniq-navy-900)] text-white'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`}
                  aria-label="Table view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>

            {!hasFilterMatches ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                No competitors match the current search and filters.
              </div>
            ) : view === 'card' ? (
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {filteredAndSortedCompetitors.map((competitor) => {
                  const score = Number(competitor.threat_score ?? 0)
                  const threatClasses = threatScoreClasses(score)
                  const threatTier = threatTierMeta(competitor.threat_tier)
                  const decisionBadge = decisionBadgeMeta(score)
                  const bsrTrend = (competitor.bsr_trend_30d ?? [])
                    .map((point) => Number(point.avg_bsr))
                    .filter((value) => Number.isFinite(value))
                  const scoreHistoryArr = (competitor.score_history ?? [])
                    .map((point) => Number(point.total_score ?? 0))
                    .filter((value) => Number.isFinite(value))
                  const scoreDelta = scoreHistoryArr.length >= 2
                    ? Math.round(scoreHistoryArr[scoreHistoryArr.length - 1] - scoreHistoryArr[0])
                    : null
                  const scoreHistory = scoreHistoryArr.slice(-7)

                  let sparklineValues: number[] = []
                  let sparklineLabel = '30-day Amazon rank trend'
                  let sparklineKind: SparklineKind = 'bsr'

                  if (bsrTrend.length >= 2) {
                    sparklineValues = bsrTrend
                  } else if (scoreHistory.length > 0) {
                    sparklineValues = scoreHistory
                    sparklineLabel = '7-day threat score trend'
                    sparklineKind = 'score'
                  }

                  const showAwaitingFirstScan = sparklineValues.length === 0
                  const normalizedHandle = (competitor.tiktok_handle ?? '').replace(/^@/, '').trim()
                  const overallBsr = normalizeNumericValue(competitor.latest_bsr_overall)
                  const previousBsr = bsrTrend.length >= 2 ? bsrTrend[bsrTrend.length - 2] : null
                  const hasSubcategoryBsr =
                    Boolean(competitor.key_asin) && competitor.latest_bsr_subcategory !== null
                  const hasBsrSnapshot = overallBsr !== null || hasSubcategoryBsr
                  const estimatedMonthlySales = estimateMonthlySales(overallBsr)
                  const pendingActionCount = pendingActionCountByCompetitor.get(competitor.id) ?? 0
                  const averagePurchaseIntent = purchaseIntentByCompetitor.get(competitor.id) ?? 0
                  const creatorSignal = creatorSignalByCompetitor.get(competitor.id) ?? null
                  const topSignalSummary = generateSignalSummary(
                    competitor.top_signal_summary || '',
                    creatorSignal?.creatorCount ?? 0,
                    creatorSignal?.paidDisclosureRate ?? null,
                    overallBsr,
                    previousBsr,
                    competitor.brand_name
                  )

                  return (
                    <div
                      key={competitor.id}
                      className={`group overflow-hidden rounded-xl border border-slate-200 border-l-[5px] ${threatClasses.cardBg} shadow-sm transition-shadow hover:shadow-md ${threatClasses.cardHover} ${threatClasses.panel}`}
                    >
                      <div className="flex items-start justify-between gap-3 px-5 pb-4 pt-5">
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-base font-bold leading-snug text-slate-900">
                            {competitor.brand_name}
                          </h3>
                          <div className="mt-2">
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold ${threatTier.pill}`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${threatTier.dot}`} />
                              {threatTier.label}
                            </span>
                            {pendingActionCount > 0 && (
                              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                {pendingActionCount} action{pendingActionCount > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="shrink-0 text-right">
                          <div className="relative inline-flex items-center justify-center">
                            {score >= 60 && (
                              <div className="absolute inset-0 -m-2 rounded-full bg-red-100/60" />
                            )}
                            {score >= 40 && score < 60 && (
                              <div className="absolute inset-0 -m-2 rounded-full bg-amber-100/50" />
                            )}
                            <div className={`relative text-3xl font-bold leading-none tabular-nums ${threatClasses.text}`}>
                              {Math.round(score)}
                            </div>
                          </div>
                          <div className={`mt-1 text-xs font-semibold ${decisionBadge.pill}`}>
                            {decisionBadge.label}
                          </div>
                          <div className="mt-1">
                            {(() => {
                              const pred = predictions.find(p => p.competitor_id === competitor.id)
                              return pred ? <PredictionBadge probability={pred.probability_top5_90d} size="sm" /> : null
                            })()}
                          </div>
                          {scoreDelta !== null && scoreDelta !== 0 && (
                            <div className={`mt-1 text-xs font-semibold ${scoreDelta > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                              {scoreDelta > 0 ? '↑' : '↓'} {Math.abs(scoreDelta)} this week
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-3">
                        <div className="flex items-start gap-2 text-sm leading-snug text-slate-700">
                          <span className="mt-px shrink-0">{channelIcon(competitor.top_signal_channel)}</span>
                          <span className="line-clamp-2">{topSignalSummary}</span>
                        </div>
                      </div>

                      <div className="border-t border-slate-100 px-5 py-3">
                        <PurchaseIntentIndicator score={averagePurchaseIntent} compact />
                      </div>

                      <div className="border-t border-slate-100 px-5 py-3">
                        {showAwaitingFirstScan ? (
                          <div className="flex items-center gap-1.5 text-sm text-slate-400">
                            <Clock3 className="h-3.5 w-3.5 shrink-0" />
                            Awaiting first data scan
                          </div>
                        ) : (
                          <>
                            <Sparkline values={sparklineValues} kind={sparklineKind} />
                            <div className="mt-1 text-xs text-slate-400">{sparklineLabel}</div>
                          </>
                        )}
                      </div>

                      <div className="border-t border-slate-100 px-5 py-3">
                        {hasBsrSnapshot ? (
                          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                            {overallBsr !== null && (
                              <span className="font-semibold text-slate-800">
                                BSR {formatRank(overallBsr)}
                              </span>
                            )}
                            {hasSubcategoryBsr && competitor.subcategory_name ? (
                              <span className="text-slate-500">
                                · {formatRank(normalizeNumericValue(competitor.latest_bsr_subcategory))} in {competitor.subcategory_name}
                              </span>
                            ) : null}
                            {estimatedMonthlySales ? (
                              <span className="text-xs text-slate-400">
                                · est. {estimatedMonthlySales}/mo
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">No Amazon data yet</span>
                        )}
                      </div>

                      {(normalizedHandle || competitor.key_asin) && (
                        <div className="flex gap-4 border-t border-slate-100 px-5 py-3">
                          {normalizedHandle && (
                            <a
                              href={`https://tiktok.com/@${normalizedHandle}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              className="flex items-center gap-1 text-xs text-blue-500 underline-offset-2 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                              TikTok
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                          {competitor.key_asin && (
                            <a
                              href={`https://amazon.com/dp/${competitor.key_asin}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              className="flex items-center gap-1 text-xs text-blue-500 underline-offset-2 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                              Amazon
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      )}

                      <div className="border-t border-slate-100 px-5 py-3">
                        <button
                          type="button"
                          onClick={() => openCompetitorProfile(competitor)}
                          className="text-sm font-semibold text-[var(--patterniq-navy-900)] hover:underline"
                        >
                          View Full Profile →
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Brand Name</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Score</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Tier Badge</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">BSR Rank</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Est. Monthly Sales</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Top Signal</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Last Updated</th>
                      <th className="px-4 py-2 text-left font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedCompetitors.map((competitor) => {
                      const tierMeta = tierBadgeMeta(competitor.alert_tier)
                      const score = Number(competitor.threat_score ?? 0)
                      const scoreClass = threatScoreClasses(score).text
                      const scoreSparklineValues = (competitor.score_history ?? [])
                        .slice(-7)
                        .map((point) => Number(point.total_score ?? 0))
                        .filter((value) => Number.isFinite(value))
                      const bsr = normalizeNumericValue(competitor.latest_bsr_overall)
                      const salesEstimate = estimateMonthlySales(bsr)
                      const lastUpdated = formatDate(competitor.bsr_snapshot_at ?? competitor.added_at ?? null)
                      const bsrTrendValues = (competitor.bsr_trend_30d ?? [])
                        .map((point) => Number(point.avg_bsr))
                        .filter((value) => Number.isFinite(value))
                      const previousBsr = bsrTrendValues.length >= 2 ? bsrTrendValues[bsrTrendValues.length - 2] : null
                      const creatorSignal = creatorSignalByCompetitor.get(competitor.id) ?? null
                      const topSignalSummary = generateSignalSummary(
                        competitor.top_signal_summary || '',
                        creatorSignal?.creatorCount ?? 0,
                        creatorSignal?.paidDisclosureRate ?? null,
                        bsr,
                        previousBsr,
                        competitor.brand_name
                      )

                      return (
                        <tr key={competitor.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-900">{competitor.brand_name}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold tabular-nums ${scoreClass}`}>
                                {Math.round(score)}
                              </span>
                              <Sparkline values={scoreSparklineValues} kind="score" width={40} height={16} compact />
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tierMeta.badge}`}
                            >
                              {tierMeta.label}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-slate-700">{formatRank(bsr)}</td>
                          <td className="px-4 py-2 text-slate-700">{salesEstimate || '—'}</td>
                          <td className="px-4 py-2 text-slate-700">
                            <span className="block max-w-[340px] truncate">
                              {truncateSignalForTable(topSignalSummary)}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-slate-700">{lastUpdated}</td>
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => openCompetitorProfile(competitor)}
                              className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                              View Full Profile →
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </section>

      <CompetitorDetailDrawer
        open={isDrawerOpen}
        competitor={selectedCompetitor}
        detail={selectedCompetitorDetail}
        loading={detailLoading}
        error={competitorDetailError}
        onClose={() => setIsDrawerOpen(false)}
      />

      <AddCompetitorModal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} />
    </>
  )
}
